import React, { useState, useEffect, useRef } from 'react';
import Search from '@carvana/core-helios-search';
import { LoadingSpinner } from '@carvana/core-helios-loader';
import clsx from 'clsx';
import { useRepairFacilities } from '@components/service-and-repair/providers/repair-facilities/repair-facilities-provider';
import { AnalyticEvents } from '@constants/index';
import { Analytics } from '@services/analytics-service';
import { AutocompleteAddress } from '@components/service-and-repair/services/user-profile-service';
import { datadogRum } from '@datadog/browser-rum';
import { getUserState } from '@carvana/core-auth';
import { SarStore } from '@store/sar-store';
import { useUserSelections } from '@components/service-and-repair/providers/user-selections/user-selections-provider';
import { getApiBaseUrl } from '@components/service-and-repair/services/base-url-config';
import { ObserverWrapper } from './observer-wrapper';
import styles from './preferred-partner-form.module.css';
import ResultTile, { ResultTileProps } from './result-tile';
import NoResultsIcon from './icons/no-results-icon.svg';
import '@carvana/core-helios-search/index.css';

const getAddressString = (addressGroup): string => {
  const { streetAddress, city, state, zip5 } = addressGroup || {};
  if (!streetAddress) return '';

  return `${streetAddress}, ${city}, ${state} ${zip5}`;
};

type PreferredPartnerFormProps = {
  heading: string;
};

export const PreferredPartnerForm = ({ heading }: PreferredPartnerFormProps) => {
  const { isLoading, userAddress, repairFacilities, experimentName, bucket, fetchRepairFacilities } = useRepairFacilities();
  const { asrId } = useUserSelections();
  const autoCompleteAddressRef = useRef<HTMLInputElement>(null);
  const [options, setOptions] = useState<AutocompleteAddress[] | null>(null);
  const [selectedAddress, setSelectedAddress] = useState<AutocompleteAddress | null | undefined>(userAddress);

  const hasResults = repairFacilities.length > 0;
  const inputDisplay = getAddressString(selectedAddress?.addressGroup) || '';

  const handleAddressChange = async (newValue) => {
    const ddAction = 'fetch-address-autocomplete';
    const endpoint = `${getApiBaseUrl()}/core/contacts/api/v1/details/addresses/suggest`;

    if (newValue === '') {
      return;
    }

    const params = new URLSearchParams({
      partialStreetAddress: newValue,
    });
    try {
      datadogRum.addAction(ddAction, { type: 'start', sarSessionId: SarStore.sarSessionId });
      const response = await fetch(`${endpoint}?${params.toString()}`, {
        method: 'GET',
      });

      if (!response.ok) {
        const { message } = (response as any) || {};
        datadogRum.addAction(ddAction, { type: 'error', error: message, sarSessionId: SarStore.sarSessionId });
        throw new Error(message || 'Failed to fetch data');
      }

      const res = await response.json();
      const newOptions = (res?.content || []).map((addressGroup) => {
        return {
          addressGroup,
          fullSuggestedAddress: getAddressString(addressGroup),
        };
      });

      setOptions(newOptions);
      datadogRum.addAction(ddAction, { type: 'success', sarSessionId: SarStore.sarSessionId });
    } catch (error: unknown) {
      datadogRum.addAction(ddAction, { type: 'error', error, sarSessionId: SarStore.sarSessionId });
    }
  };

  const handleClearInput = () => {
    setSelectedAddress(null);
    setOptions([]);
  };

  const handleAddressSelection = (value) => {
    const fullAddressData = options?.find((opt) => opt?.fullSuggestedAddress === value);
    setSelectedAddress(fullAddressData);

    if (fullAddressData) {
      if (asrId === null || asrId === undefined) {
        datadogRum.addAction(AnalyticEvents.sarFetchFacilitiesNullAsrId, {
          type: 'warning',
          message: 'fetchRepairFacilities called without asrId - context asrId is null',
          sarSessionId: SarStore.sarSessionId,
        });
      }
      fetchRepairFacilities(fullAddressData);
    }

    Analytics.trackEvent(AnalyticEvents.sarCoverageScreenAddressChanged, {
      variant: 2,
    });
  };

  const handleViewedEvent = (tile: ResultTileProps) => {
    const props = {
      variant: 2,
      vendorId: tile.vendorId,
      name: tile.name,
      drivingMiles: tile?.distanceFromLocation,
      rating: tile.rating,
      hasScheduleAppointment: tile.buttonType === 'appointment',
      isNational: tile.isNational ?? null,
      asrId: asrId ?? null,
      coverageScreen: SarStore.coverageScreenType || null,
      userId: getUserState()?.user?.user_id || null,
      sarSessionId: SarStore.sarSessionId || null,
      experimentName: experimentName || null,
      bucket: bucket || null,
    };
    Analytics.trackEvent(AnalyticEvents.sarCoverageScreenRepairFacilityViewed, props);
    datadogRum.addAction(AnalyticEvents.sarCoverageScreenRepairFacilityViewed, props);
  };

  useEffect(() => {
    const trackFocusEvent = () => {
      Analytics.trackEvent(AnalyticEvents.sarCoverageScreenAddressFocused, { variant: 2 });
    };
    if (autoCompleteAddressRef.current) autoCompleteAddressRef.current.addEventListener('focus', trackFocusEvent);

    return () => {
      if (autoCompleteAddressRef.current) autoCompleteAddressRef.current.removeEventListener('focus', trackFocusEvent);
    };
  }, []);

  const normalizedOptions = (options || []).map((option) => option.fullSuggestedAddress);

  return (
    <div className={clsx(styles['repair-shop-list'], styles['search-input'])}>
      <div className={styles['search-location']}>
        <span className={styles['heading']}>{heading}</span>

        <Search
          id="search-address"
          placeholder="Search an address"
          className={styles['search-input']}
          value={inputDisplay}
          onChange={handleAddressChange}
          onSelect={handleAddressSelection}
          onClearInput={handleClearInput}
          debounceDelay={250}
          ref={autoCompleteAddressRef}
          options={options ? normalizedOptions : null}
        />
      </div>
      <div className={styles['search-tiles']}>
        {isLoading ? (
          <div className={styles['loading-spinner']}>
            <LoadingSpinner />
          </div>
        ) : (
          <>
            {hasResults ? (
              <>
                {repairFacilities.map((tile: any, index) => (
                  // Only ResultTile entries as Form Results should trigger the Viewed Event
                  <ObserverWrapper key={index} onViewed={() => handleViewedEvent(tile)}>
                    <ResultTile {...tile} />
                  </ObserverWrapper>
                ))}
              </>
            ) : (
              <div className={styles['no-results']}>
                <img src={NoResultsIcon} alt="no-results" className={styles['no-results-icon']} />
                <span className={styles['no-results-heading']}>No results found</span>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
